// src/client/renderer/vfx/items/item-vfx-manager.ts
//
// Plan 17, Task 1.1 — ItemVfxManager skeleton.
// Plan 17, Task 6.1 — APPENDED: createItemVfxPools + createItemVfxEventResolver
//   bundle factories (APPEND-ONLY — do NOT edit the existing createItemVfxManager body).
//
// Design mirrors tile-vfx-manager.ts:
//   - per-pool active-set Map keyed by poolId
//   - closure-stored subscriberId
//   - idempotent disposed flag guard
//   - pool.update(dt) → ReadonlySet iteration per tick
//
// Extension beyond tile-manager:
//   Fires `audio.onAudioCue(ITEM_VFX_AUDIO_CUES[poolId])` only for cast-phase
//   spawns (after resolver hit, before scene.add). Per-pool multi-phase
//   emissions (active → impact → tick) do NOT replay the cast cue.

import * as THREE from 'three';
import type {
  ItemVfxPoolId,
  ItemVfxPool,
  ItemVfxSpawnArgs,
  ItemVfxManager,
  ItemVfxManagerConfig,
  ItemVfxEventResolver,
  ItemVfxPoolDeps,
} from './item-vfx-types';
import type { GameEvent } from '../../../../shared/game/types';
import type { Npc } from '../../../../shared/game/npc/types';
import { NO_OP_AUDIO_HOOKS } from '../audio-hooks';
import { ITEM_VFX_AUDIO_CUES } from './audio-cues';
import { applyItemVfxSpriteMasks } from './sprite-textures';
// Per-pool factories (Task 6.1 bundle imports)
import { createThunderFistPool, resolveThunderFistForEvent } from './thunder-fist';
import { createFrostBlasterPool, resolveFrostBlasterForEvent } from './frost-blaster';
import { createMeteorStrikePool, resolveMeteorStrikeForEvent } from './meteor-strike';
import { createOrbitalCannonPool, resolveOrbitalCannonForEvent } from './orbital-cannon';
import { createToxicCloudPool, resolveToxicCloudForEvent } from './toxic-cloud';
import { createGravityWellPool, resolveGravityWellForEvent } from './gravity-well';
import { createLightningDashPool, resolveLightningDashForEvent } from './lightning-dash';
import { createPhoenixPotionPool, resolvePhoenixPotionForEvent } from './phoenix-potion';
import { createStoneSkinPool, resolveStoneSkinForEvent } from './stone-skin';
import { createDimensionRiftPool, resolveDimensionRiftForEvent } from './dimension-rift';
import { createMysteryBoxPool, resolveMysteryBoxForEvent } from './mystery-box';
import { createShadowStalkerPool, resolveShadowStalkerForEvent } from './shadow-stalker';
import { createCursedRelicPool, resolveCursedRelicForEvent } from './cursed-relic';
import { createSoulDuelPool, resolveSoulDuelForEvent } from './soul-duel';

// ─────────────────────────────────────────────────────────────────────────────
// createItemVfxManager
// ─────────────────────────────────────────────────────────────────────────────

export function createItemVfxManager(config: ItemVfxManagerConfig): ItemVfxManager {
  const { scene, eventLog, pools, resolver } = config;
  const audio = config.audio ?? NO_OP_AUDIO_HOOKS;

  // Per-pool active emitter tracking.
  // Manager-side set tracks emitters that have been added to the scene;
  // pool.update(dt) returns the authoritative still-active set from the pool's
  // perspective, which we use on each tick to discover newly-finished emitters.
  const activeByPool = new Map<ItemVfxPoolId, Set<THREE.Object3D>>();
  for (const [poolId] of pools) {
    activeByPool.set(poolId, new Set<THREE.Object3D>());
  }

  let disposed = false;

  // ── Event subscriber ───────────────────────────────────────────────────────

  function handleEvent(event: GameEvent): void {
    const result = resolver(event);
    if (result === null) return;
    spawn(result.poolId, result.args);
  }

  const subscriberId = eventLog.subscribe(handleEvent);

  // ── spawn ──────────────────────────────────────────────────────────────────

  function spawn(poolId: ItemVfxPoolId, args: ItemVfxSpawnArgs): void {
    const pool = pools.get(poolId);
    if (!pool) return; // unknown poolId — no-op

    const emitter = pool.acquire(args);
    if (emitter === null) return; // pool full — no-op (no audio, no scene.add)

    applyItemVfxSpriteMasks(emitter);

    // Fire item SFX only for cast-phase visuals. Hit/miss/tick phases can spawn
    // additional emitters, but should not replay the cast sound.
    if (args.phase === 'cast') {
      audio.onAudioCue(ITEM_VFX_AUDIO_CUES[poolId]);
    }

    scene.add(emitter);

    let activeSet = activeByPool.get(poolId);
    if (!activeSet) {
      // Defensive: pool was added after construction (shouldn't normally happen)
      activeSet = new Set<THREE.Object3D>();
      activeByPool.set(poolId, activeSet);
    }
    activeSet.add(emitter);
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): void {
    for (const [poolId, activeSet] of activeByPool) {
      const pool = pools.get(poolId);
      if (!pool) continue;

      // Per-pool update — returns the set of emitters STILL active after this tick.
      // This is the authoritative list; we do NOT call pool.update(emitter, dt) per-emitter.
      const stillActive = pool.update(deltaSeconds);

      // Walk the pool's returned active set to detect newly-finished emitters.
      const toRemove: THREE.Object3D[] = [];
      for (const emitter of stillActive) {
        if (pool.isFinished(emitter)) {
          toRemove.push(emitter);
        }
      }
      // Also handle emitters in manager set that pool no longer reports at all
      // (pool already recycled them without going through isFinished=true path).
      for (const emitter of activeSet) {
        if (!stillActive.has(emitter) && !toRemove.includes(emitter)) {
          toRemove.push(emitter);
        }
      }

      for (const emitter of toRemove) {
        if (activeSet.has(emitter)) {
          scene.remove(emitter);
          pool.release(emitter);
          activeSet.delete(emitter);
        }
      }

      // Sync manager set to match what the pool says is still running
      // (add any emitters the pool reports but manager doesn't yet know about)
      for (const emitter of stillActive) {
        if (!pool.isFinished(emitter) && !activeSet.has(emitter)) {
          activeSet.add(emitter);
        }
      }
    }
  }

  // ── activeCount ────────────────────────────────────────────────────────────

  function getActiveCount(): number {
    let total = 0;
    for (const activeSet of activeByPool.values()) {
      total += activeSet.size;
    }
    return total;
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return; // idempotent guard
    disposed = true;

    // Unsubscribe from event bus
    eventLog.unsubscribe(subscriberId);

    // Call pool.dispose() on every pool (final GPU teardown) and clear active sets
    for (const [poolId, activeSet] of activeByPool) {
      const pool = pools.get(poolId);
      if (pool) {
        // Remove all live emitters from the scene before pool disposal
        for (const emitter of activeSet) {
          scene.remove(emitter);
        }
        pool.dispose();
      }
      activeSet.clear();
    }
  }

  return {
    spawn,
    update,
    get activeCount() {
      return getActiveCount();
    },
    dispose,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// createItemVfxPools — bundle factory for ALL 14 item-VFX pools
// ─────────────────────────────────────────────────────────────────────────────
//
// Plan 17, Task 6.1 — APPENDED below existing createItemVfxManager.
//
// KEY DIFFERENCE from tile-VFX (F7):
//   There is NO tile_bounce-style exclusion here. ALL 14 item pools are
//   routed through the manager's resolver and manager.spawn() path. The
//   manager calls scene.add(emitter) for every successful pool.acquire() result.
//   Do NOT invent F7-style carve-outs for item pools.
//
// All 14 pools are instantiated with the shared { scene, registry } deps.
// The GsapRegistry is advanced ONCE per frame by the caller (item-vfx-manager
// via build-board.ts itemVfxState.registry.update(dt)).
//
// Resolver tick contract (HF1):
//   Each per-pool factory's update() IGNORES dt — does NOT advance the registry.
//   Only the caller (ItemVfxManager → GsapRegistry.update) advances it once
//   per frame across all pools. This prevents the ~14× tick-rate multiplication
//   that would occur if each pool called registry.update independently.

/**
 * Bundle factory: returns ALL 14 item-VFX pools instantiated with the shared
 * scene + GsapRegistry. Returned Map is frozen (Object.freeze).
 *
 * Call site:
 *   const registry = createGsapRegistry();
 *   const pools = createItemVfxPools({ scene, registry });
 *   const resolver = createItemVfxEventResolver({ ... });
 *   const manager = createItemVfxManager({ scene, eventLog, pools, resolver, audio });
 */
export function createItemVfxPools(deps: ItemVfxPoolDeps): ReadonlyMap<ItemVfxPoolId, ItemVfxPool> {
  const pools = new Map<ItemVfxPoolId, ItemVfxPool>([
    ['thunder_fist', createThunderFistPool(deps)],
    ['frost_blaster', createFrostBlasterPool(deps)],
    ['meteor_strike', createMeteorStrikePool(deps)],
    ['orbital_cannon', createOrbitalCannonPool(deps)],
    ['toxic_cloud', createToxicCloudPool(deps)],
    ['gravity_well', createGravityWellPool(deps)],
    ['lightning_dash', createLightningDashPool(deps)],
    ['phoenix_potion', createPhoenixPotionPool(deps)],
    ['stone_skin', createStoneSkinPool(deps)],
    ['dimension_rift', createDimensionRiftPool(deps)],
    ['mystery_box', createMysteryBoxPool(deps)],
    ['shadow_stalker', createShadowStalkerPool(deps)],
    ['cursed_relic', createCursedRelicPool(deps)],
    ['soul_duel', createSoulDuelPool(deps)],
  ]);

  return Object.freeze(pools);
}

// ─────────────────────────────────────────────────────────────────────────────
// createItemVfxEventResolver — composed resolver for all 14 item pools
// ─────────────────────────────────────────────────────────────────────────────
//
// Resolution order (first-match-wins):
//   thunder_fist → frost_blaster → meteor_strike → orbital_cannon →
//   toxic_cloud → gravity_well → lightning_dash → phoenix_potion →
//   stone_skin → dimension_rift → mystery_box → shadow_stalker →
//   cursed_relic → soul_duel
//
// The order is mostly cosmetic because each per-item resolver immediately returns
// null when event.itemId does not match — there are no ambiguous cross-item
// events. The documented order ensures stable, auditable behaviour for any
// future cross-item events (e.g. ItemCastEvent dispatched by a chain-item
// mechanism). First-match-wins eliminates any ambiguity.
//
// Callback opts:
//   getPositionForPlayer?: (playerId) → {x,y,z} | undefined
//   getPositionForNode?:   (nodeId)   → {x,y,z} | undefined
//   getNpcById?:           (npcId)    → Npc | undefined
//
// Note: resolveCursedRelicForEvent takes `getPositionForVictim` as its third
// arg (separate from `getPositionForPlayer`). We pass the same getPositionForPlayer
// callback for both positions — consistent with the spec which says both are
// player-position lookups. If per-victim precision is needed later, a follow-up
// can thread a dedicated callback.

export interface CreateItemVfxEventResolverOpts {
  /** Plan 22 hook: resolve playerId → world position. */
  readonly getPositionForPlayer?: (
    playerId: string,
  ) => { x: number; y: number; z: number } | undefined;
  /** Plan 22 hook: resolve nodeId → world position. */
  readonly getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined;
  /** Optional item model cache lookup. Returns only already-resolved templates. */
  readonly getItemModelScene?: (itemId: string) => THREE.Object3D | null;
  /** Plan 22 hook: resolve npcId → Npc. Used by Shadow Stalker ghost positioning. */
  readonly getNpcById?: (npcId: string) => Npc | undefined;
}

/**
 * Bundle factory: returns the composed event resolver that walks all 14 per-item
 * resolvers in priority order (first-match-wins).
 *
 * ALL 14 pools are routed through this resolver — there is no F7-style exclusion
 * for item-VFX (unlike tile-VFX where tile_bounce bypasses the manager).
 */
export function createItemVfxEventResolver(
  opts: CreateItemVfxEventResolverOpts,
): ItemVfxEventResolver {
  const { getPositionForPlayer, getPositionForNode, getItemModelScene, getNpcById } = opts;

  return function resolveItemVfxEvent(
    event: GameEvent,
  ): { poolId: ItemVfxPoolId; args: ItemVfxSpawnArgs } | null {
    // ── 1. thunder_fist ────────────────────────────────────────────────────
    const thunderFist = resolveThunderFistForEvent(
      event,
      getPositionForPlayer,
      getPositionForNode,
      getItemModelScene,
    );
    if (thunderFist !== null) return thunderFist;

    // ── 2. frost_blaster ───────────────────────────────────────────────────
    const frostBlaster = resolveFrostBlasterForEvent(event, getPositionForPlayer);
    if (frostBlaster !== null) return frostBlaster;

    // ── 3. meteor_strike ───────────────────────────────────────────────────
    const meteorStrike = resolveMeteorStrikeForEvent(event, getPositionForPlayer);
    if (meteorStrike !== null) return meteorStrike;

    // ── 4. orbital_cannon ──────────────────────────────────────────────────
    const orbitalCannon = resolveOrbitalCannonForEvent(event, getPositionForPlayer);
    if (orbitalCannon !== null) return orbitalCannon;

    // ── 5. toxic_cloud ─────────────────────────────────────────────────────
    const toxicCloud = resolveToxicCloudForEvent(event, getPositionForNode);
    if (toxicCloud !== null) return toxicCloud;

    // ── 6. gravity_well ────────────────────────────────────────────────────
    const gravityWell = resolveGravityWellForEvent(event, getPositionForPlayer);
    if (gravityWell !== null) return gravityWell;

    // ── 7. lightning_dash ──────────────────────────────────────────────────
    const lightningDash = resolveLightningDashForEvent(
      event,
      getPositionForPlayer,
      getPositionForNode,
    );
    if (lightningDash !== null) return lightningDash;

    // ── 8. phoenix_potion ──────────────────────────────────────────────────
    const phoenixPotion = resolvePhoenixPotionForEvent(event, getPositionForPlayer);
    if (phoenixPotion !== null) return phoenixPotion;

    // ── 9. stone_skin ──────────────────────────────────────────────────────
    const stoneSkin = resolveStoneSkinForEvent(event, getPositionForPlayer);
    if (stoneSkin !== null) return stoneSkin;

    // ── 10. dimension_rift ─────────────────────────────────────────────────
    const dimensionRift = resolveDimensionRiftForEvent(
      event,
      getPositionForPlayer,
      getPositionForNode,
    );
    if (dimensionRift !== null) return dimensionRift;

    // ── 11. mystery_box ────────────────────────────────────────────────────
    const mysteryBox = resolveMysteryBoxForEvent(event, getPositionForPlayer, getPositionForNode);
    if (mysteryBox !== null) return mysteryBox;

    // ── 12. shadow_stalker ─────────────────────────────────────────────────
    // Note: resolveShadowStalkerForEvent takes getPositionForPlayer + getPositionForNode + getNpcById
    const shadowStalker = resolveShadowStalkerForEvent(
      event,
      getPositionForPlayer,
      getPositionForNode,
      getNpcById,
    );
    if (shadowStalker !== null) return shadowStalker;

    // ── 13. cursed_relic ───────────────────────────────────────────────────
    // Note: resolveCursedRelicForEvent takes getPositionForPlayer + getPositionForVictim.
    // Both are passed the same getPositionForPlayer callback — victim is also a
    // player in Cursed Relic context. Follow-up F2.x: thread dedicated callback if needed.
    const cursedRelic = resolveCursedRelicForEvent(
      event,
      getPositionForPlayer,
      getPositionForPlayer,
    );
    if (cursedRelic !== null) return cursedRelic;

    // ── 14. soul_duel ──────────────────────────────────────────────────────
    const soulDuel = resolveSoulDuelForEvent(event, getPositionForPlayer);
    if (soulDuel !== null) return soulDuel;

    return null;
  };
}
